import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Grid, 
  IconButton, 
  TextField, 
  Typography,
  InputAdornment,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from "../../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../../state/ModalSlice/ModalSlice.ts";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { toast } from 'react-toastify';
import { collection, addDoc, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { selectEffectiveUID, selectUserSliceV2Data } from '../../../../state/UserStatesV2/userSliceV2';
import CountryCodeSelect, { 
  validatePhoneNumber, 
  getPhoneNumberPlaceholder, 
  formatPhoneNumber,
  countryCodes 
} from '../../Components/CountryCodeSelect';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    padding: theme.spacing(2),
    minWidth: '400px',
    maxWidth: '500px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const PhoneInputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(2)
}));

const StyledCountryCodeSelect = styled(CountryCodeSelect)({
  '& .MuiOutlinedInput-root': {
    height: '40px',
    '& .MuiSelect-select': {
      padding: '8.5px 14px',
      height: '40px !important',
      display: 'flex',
      alignItems: 'center'
    }
  }
});

interface PaymentHeader {
  id: string;
  name: string;
  defaultAmount?: number;
  description?: string;
}

interface FormData {
  name: string;
  phoneNumber: string;
  amount: string;
  description: string;
  countryCode: string;
  selectedHeader: string;
  category: string;
}

export default function PaymentRequestModal() {
  const open = useAppSelector((state: RootState) => state.ModalSlice.PaymentRequestModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const serviceProviderId = useAppSelector(selectEffectiveUID);
  const userData = useAppSelector(selectUserSliceV2Data);
  const dispatch = useAppDispatch();

  const [paymentHeaders, setPaymentHeaders] = useState<PaymentHeader[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phoneNumber: '',
    amount: '',
    description: '',
    countryCode: '+91',
    selectedHeader: '',
    category: ''
  });

  // Load payment headers when modal opens
  useEffect(() => {
    if (open && serviceProviderId) {
      loadPaymentHeaders();
    }
  }, [open, serviceProviderId]);

  const loadPaymentHeaders = async () => {
    try {
      const spDoc = await getDoc(doc(db, 'ServiceProviders', serviceProviderId));
      if (spDoc.exists()) {
        const data = spDoc.data();
        setPaymentHeaders(data.paymentHeaders || []);
      }
    } catch (error) {
      console.error('Error loading payment headers:', error);
    }
  };

  const handleClose = () => {
    dispatch(closeModal("PaymentRequestModalOpen"));
    setFormData({
      name: '',
      phoneNumber: '',
      amount: '',
      description: '',
      countryCode: '+91',
      selectedHeader: '',
      category: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const country = countryCodes.find((c: { dialCode: string }) => c.dialCode === formData.countryCode);
    
    if (!country) return;

    // Remove any non-digit characters
    const cleanValue = value.replace(/\D/g, '');
    
    // Get max length based on country
    const maxLength = Array.isArray(country.phoneLength) 
      ? country.phoneLength[1] 
      : country.phoneLength;

    // Only update if within length limit
    if (cleanValue.length <= maxLength) {
      setFormData(prev => ({
        ...prev,
        phoneNumber: cleanValue
      }));
    }
  };

  const handleHeaderSelect = (headerId: string) => {
    const selectedHeader = paymentHeaders.find(h => h.id === headerId);
    if (selectedHeader) {
      setFormData(prev => ({
        ...prev,
        selectedHeader: headerId,
        category: selectedHeader.name,
        amount: selectedHeader.defaultAmount ? selectedHeader.defaultAmount.toString() : prev.amount,
        description: selectedHeader.description || prev.description
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        selectedHeader: '',
        category: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phoneNumber || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate phone number based on country code
    if (!validatePhoneNumber(formData.phoneNumber, formData.countryCode)) {
      toast.error(`Please enter a valid phone number for ${formData.countryCode}`);
      return;
    }

    // Validate amount (should be a positive number)
    if (isNaN(Number(formData.amount)) || Number(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      if (!serviceProviderId) {
        throw new Error("Service provider ID not found");
      }

      const transactionData = {
        payerName: formData.name,
        phoneNumber: formatPhoneNumber(formData.phoneNumber, formData.countryCode),
        countryCode: formData.countryCode,
        amount: Number(formData.amount),
        description: formData.description,
        status: 'Pending',
        type: 'Credit',
        createdAt: Timestamp.now(),
        recordedBy: userData?.uid || 'unknown',
        category: formData.category || undefined
      };

      const transactionsRef = collection(db, 'ServiceProviders', serviceProviderId, 'Adhoc');
      await addDoc(transactionsRef, transactionData);
      
      toast.success("Payment request sent successfully!");
      handleClose();
    } catch (error) {
      console.error("Error sending payment request:", error);
      toast.error("Failed to send payment request. Please try again.");
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Send Payment Request</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 0 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Recipient Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={isLoading}
                size="small"
              />
            </Grid>
            
            {/* Payment Category Selection */}
            {paymentHeaders.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Category (Optional)</InputLabel>
                  <Select
                    value={formData.selectedHeader}
                    label="Payment Category (Optional)"
                    onChange={(e) => handleHeaderSelect(e.target.value as string)}
                    disabled={isLoading}
                  >
                    <MenuItem value="">
                      <em>None - Custom Payment</em>
                    </MenuItem>
                    {paymentHeaders.map((header) => (
                      <MenuItem key={header.id} value={header.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <span>{header.name}</span>
                          {header.defaultAmount && (
                            <Chip 
                              label={`₹${header.defaultAmount.toLocaleString()}`} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {formData.selectedHeader && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Selected: {formData.category}
                    {paymentHeaders.find(h => h.id === formData.selectedHeader)?.description && 
                      ` - ${paymentHeaders.find(h => h.id === formData.selectedHeader)?.description}`
                    }
                  </Typography>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <PhoneInputContainer>
                <StyledCountryCodeSelect
                  value={formData.countryCode}
                  onChange={(value) => setFormData({...formData, countryCode: value})}
                  label="Country Code"
                  required
                  fullWidth
                />
                <StyledTextField
                required
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder={getPhoneNumberPlaceholder(formData.countryCode)}
                disabled={isLoading}
                  size="small"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    maxLength: (() => {
                      const country = countryCodes.find((c: { dialCode: string }) => c.dialCode === formData.countryCode);
                      if (!country) return 15;
                      return Array.isArray(country.phoneLength) ? country.phoneLength[1] : country.phoneLength;
                    })()
                  }}
                  helperText={`Format: ${getPhoneNumberPlaceholder(formData.countryCode)}`}
              />
              </PhoneInputContainer>
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                required
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                disabled={isLoading}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={isLoading}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                startIcon={isLoading ? <CircularProgress size={24} color="inherit" /> : <WhatsAppIcon />}
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                {isLoading ? 'Sending...' : 'Send Payment Request'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
} 